import { Link, useNavigate } from "react-router-dom";
import { Heart, MapPin, Bed, Bath, Maximize, BadgeCheck, Sparkles, GitCompareArrows } from "lucide-react";
import { CARD } from "@/constants/testIds";
import { formatPrice, COUNTRY_FLAGS, api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompare } from "@/contexts/CompareContext";
import { useState } from "react";
import { toast } from "sonner";

export const PropertyCard = ({ listing, index = 0, onUnfavorite }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const compare = useCompare();
  const inCompare = compare?.ids.includes(listing.id) ?? false;
  const [favorited, setFavorited] = useState(listing._favorited ?? false);
  const [busy, setBusy] = useState(false);

  const toggleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Connectez-vous pour sauvegarder un favori");
      return;
    }
    setBusy(true);
    try {
      if (favorited) {
        await api.delete(`/favorites/${listing.id}`);
        setFavorited(false);
        onUnfavorite?.(listing.id);
      } else {
        await api.post(`/favorites/${listing.id}`);
        setFavorited(true);
      }
    } catch {
      toast.error("Erreur sur les favoris");
    } finally {
      setBusy(false);
    }
  };

  const toggleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!compare) return;
    if (!inCompare && compare.full) {
      toast.error(`Maximum ${compare.max} annonces à comparer`);
      return;
    }
    compare.toggle(listing.id);
  };

  const photo = listing.photos?.[0] || "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=1200";

  return (
    <Link
      to={`/listings/${listing.id}`}
      data-testid={CARD.link}
      className="group block fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 400)}ms` }}
    >
      <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
        <div className="aspect-[4/3] overflow-hidden bg-[#F5F0EB] relative">
          <img
            src={photo}
            alt={listing.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute top-3 left-3 flex gap-2">
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[#1A1A1A] flex items-center gap-1">
              {COUNTRY_FLAGS[listing.country]} {listing.type === "vente" ? "Vente" : "Location"}
            </span>
            {listing.is_boosted && (
              <span
                data-testid={CARD.boostedBadge}
                className="text-xs font-medium px-2.5 py-1 rounded-full bg-gradient-to-r from-[#F4B400] to-[#D6202C] text-white flex items-center gap-1 shadow"
              >
                <Sparkles className="w-3 h-3" strokeWidth={2} /> Sponsorisé
              </span>
            )}
          </div>
          <button
            onClick={toggleFav}
            disabled={busy}
            data-testid={CARD.favBtn}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
            aria-label="Favori"
          >
            <Heart
              className={`w-4 h-4 ${favorited ? "fill-[#D6202C] text-[#D6202C]" : "text-[#1A1A1A]"}`}
              strokeWidth={1.5}
            />
          </button>
          <button
            onClick={toggleCompare}
            data-testid="property-card-compare"
            className={`absolute top-14 right-3 w-9 h-9 rounded-full backdrop-blur flex items-center justify-center transition-colors ${
              inCompare ? "bg-[#11783A] text-white" : "bg-white/90 hover:bg-white text-[#1A1A1A]"
            }`}
            aria-label="Comparer"
            title={inCompare ? "Retirer de la comparaison" : "Ajouter à la comparaison"}
          >
            <GitCompareArrows className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#11783A] font-medium">
              {listing.property_type}
            </span>
            {listing.owner_badge === "pro_verified" && (
              <span data-testid={CARD.proBadge} className="flex items-center gap-1 text-[11px] text-[#11783A]">
                <BadgeCheck className="w-3.5 h-3.5" strokeWidth={2} /> Pro
              </span>
            )}
          </div>
          <h3
            data-testid={CARD.title}
            className="font-heading font-medium text-lg leading-snug mb-2 line-clamp-2 group-hover:text-[#D6202C] transition-colors"
          >
            {listing.title}
          </h3>
          <div data-testid={CARD.city} className="flex items-center gap-1 text-sm text-muted-foreground mb-4">
            <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>{listing.neighborhood ? `${listing.neighborhood}, ` : ""}{listing.city}</span>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-4 border-y border-[#F0EBE6] py-3">
            {listing.rooms != null && (
              <span className="flex items-center gap-1">
                <Bed className="w-3.5 h-3.5" strokeWidth={1.5} /> {listing.rooms} pcs
              </span>
            )}
            {listing.bathrooms != null && (
              <span className="flex items-center gap-1">
                <Bath className="w-3.5 h-3.5" strokeWidth={1.5} /> {listing.bathrooms}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Maximize className="w-3.5 h-3.5" strokeWidth={1.5} /> {Math.round(listing.surface)} m²
            </span>
          </div>

          <div className="flex items-end justify-between">
            <div>
              <div data-testid={CARD.price} className="font-heading font-semibold text-xl text-[#1A1A1A]">
                {formatPrice(listing.price, listing.currency, listing.type)}
              </div>
            </div>
            <span
              role="button"
              tabIndex={0}
              data-testid="property-card-owner"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/agence/${listing.owner_id}`); }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.stopPropagation(); navigate(`/agence/${listing.owner_id}`); } }}
              className="text-[11px] text-muted-foreground hover:text-[#D6202C] hover:underline cursor-pointer"
            >
              {listing.owner_name}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
